import React, { createContext, useContext, useState, useEffect } from "react";

const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [userLoading, setUserLoading] = useState(true);
  const [hasHydratedUser, setHasHydratedUser] = useState(false);

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("user");

      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);

        if (
          parsedUser?.expiresAt &&
          new Date(parsedUser.expiresAt).getTime() < Date.now()
        ) {
          setUser(null);
          localStorage.removeItem("user");
        } else {
          setUser(parsedUser);
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error("Failed to restore user:", error);
      setUser(null);
      localStorage.removeItem("user");
    } finally {
      setHasHydratedUser(true);
      setUserLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hasHydratedUser) return;

    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
    } else {
      localStorage.removeItem("user");
    }
  }, [user, hasHydratedUser]);

  useEffect(() => {
    const interval = setInterval(() => {
      setUser((currentUser) => {
        if (
          currentUser?.expiresAt &&
          new Date(currentUser.expiresAt).getTime() < Date.now()
        ) {
          localStorage.removeItem("user");
          return null;
        }

        return currentUser;
      });
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (user) {
      setShowAuthModal(false);
    }
  }, [user]);

  const openLoginModal = () => {
    if (userLoading || user) return;
    setShowAuthModal(true);
  };

  const closeLoginModal = () => setShowAuthModal(false);

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        userLoading,
        showAuthModal,
        openLoginModal,
        closeLoginModal,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }

  return context;
};